import { useEffect, useState } from "react";
import { Truck, Plus, X, XCircle, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const EMPTY_PT = { contact_name: "", phone: "", address: "", city: "", state: "", pincode: "" };

const PointFields = ({ title, state, setState, prefix }) => (
  <div>
    <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{title}</p>
    <div className="space-y-3 mt-2">
      {[
        { k: "contact_name", ph: "Contact person" },
        { k: "phone", ph: "Phone (10 digits)", max: 10 },
        { k: "address", ph: "Address" },
        { k: "city", ph: "City" },
        { k: "state", ph: "State" },
        { k: "pincode", ph: "Pincode", max: 6 },
      ].map((f) => (
        <input key={f.k} required value={state[f.k]} maxLength={f.max} placeholder={f.ph}
          onChange={(e) => setState((s) => ({ ...s, [f.k]: e.target.value }))}
          data-testid={`${prefix}-${f.k}`}
          className="w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
      ))}
    </div>
  </div>
);

export default function TruckBooking() {
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [vehicleId, setVehicleId] = useState("");
  const [form, setForm] = useState({ pickup: EMPTY_PT, delivery: EMPTY_PT, weight_kg: "", pieces: "1", goods_description: "", pickup_date: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    appApi.get("truck-bookings/vehicles").then((d) => setVehicles(d.vehicles || [])).catch(() => {});
    appApi.get("truck-bookings").then((d) => setBookings(d.bookings || [])).catch(() => setBookings([]));
  };
  useEffect(() => { load(); }, []);

  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel truck booking ${b.reference_no}? This cannot be undone.`)) return;
    setBusy(b.id);
    try {
      await appApi.post(`truck-bookings/${b.id}/cancel`);
      toast.success(`${b.reference_no} cancelled.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!vehicleId) {
      setError("Pick a vehicle first.");
      return;
    }
    setBusy(true);
    try {
      const d = await appApi.post("truck-bookings", {
        pickup: form.pickup,
        delivery: form.delivery,
        weight_kg: parseFloat(form.weight_kg),
        pieces: parseInt(form.pieces) || 1,
        goods_description: form.goods_description,
        vehicle_id: vehicleId,
        pickup_date: form.pickup_date,
      });
      toast.success(`Truck booked${d.reference_no ? ` — ${d.reference_no}` : ""}. Admin allocates a driver within ~30 min.`);
      setShowForm(false);
      setForm({ pickup: EMPTY_PT, delivery: EMPTY_PT, weight_kg: "", pieces: "1", goods_description: "", pickup_date: "" });
      setVehicleId("");
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="trucks-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Trucks</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Book a truck in 30 minutes</h1>
          <p className="text-sm text-zinc-500 mt-2">Tata Ace to full containers — our team allocates a verified driver and you get notified.</p>
        </div>
        <button onClick={() => setShowForm((s) => !s)} data-testid="truck-new-toggle"
          className="inline-flex items-center gap-2 bg-acid text-white text-sm font-bold px-5 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150">
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showForm ? "Cancel" : "Book Truck"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="truck-booking-form" className="border-2 border-obsidian bg-white shadow-[8px_8px_0px_0px_#2563EB]">
          <div className="px-6 py-4 border-b-2 border-obsidian bg-obsidian text-white font-bold text-sm flex items-center gap-2">
            <Truck className="w-4 h-4 text-sky-300" /> New truck booking
          </div>
          <div className="p-6 space-y-6">
            {error && <div data-testid="truck-error" className="border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">{error}</div>}

            <div>
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Vehicle</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2" data-testid="truck-vehicles">
                {vehicles.map((v) => (
                  <button type="button" key={v.id} onClick={() => setVehicleId(v.id)} data-testid={`vehicle-${v.id}`}
                    className={`border-2 border-obsidian p-4 text-left transition-all duration-150 ${vehicleId === v.id ? "bg-acid text-white shadow-[4px_4px_0px_0px_#0B1E3A]" : "bg-paper hover:bg-acid/20"}`}>
                    <Truck className="w-5 h-5 mb-2" />
                    <p className="font-bold text-sm">{v.name}</p>
                    <p className={`text-[11px] font-mono mt-1 ${vehicleId === v.id ? "text-white/80" : "text-zinc-500"}`}>
                      {v.type} · {v.size} · {v.capacity_tons}T
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <PointFields title="Pickup" state={form.pickup} setState={(fn) => setForm((s) => ({ ...s, pickup: fn(s.pickup) }))} prefix="truck-pickup" />
              <PointFields title="Delivery" state={form.delivery} setState={(fn) => setForm((s) => ({ ...s, delivery: fn(s.delivery) }))} prefix="truck-delivery" />
            </div>

            <div className="grid sm:grid-cols-4 gap-4">
              <div>
                <label htmlFor="truck-weight" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Weight (kg)</label>
                <input id="truck-weight" data-testid="truck-weight-input" required type="number" min="1" value={form.weight_kg}
                  onChange={(e) => setForm((s) => ({ ...s, weight_kg: e.target.value }))} placeholder="500"
                  className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              </div>
              <div>
                <label htmlFor="truck-pieces" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Pieces</label>
                <input id="truck-pieces" data-testid="truck-pieces-input" required type="number" min="1" value={form.pieces}
                  onChange={(e) => setForm((s) => ({ ...s, pieces: e.target.value }))}
                  className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              </div>
              <div>
                <label htmlFor="truck-date" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Pickup date</label>
                <input id="truck-date" data-testid="truck-date-input" required type="date" value={form.pickup_date}
                  onChange={(e) => setForm((s) => ({ ...s, pickup_date: e.target.value }))}
                  className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              </div>
              <div>
                <label htmlFor="truck-goods" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Goods</label>
                <input id="truck-goods" data-testid="truck-goods-input" required value={form.goods_description}
                  onChange={(e) => setForm((s) => ({ ...s, goods_description: e.target.value }))} placeholder="e.g. 20 cartons apparel"
                  className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t-2 border-obsidian flex justify-end">
            <button type="submit" disabled={busy} data-testid="truck-submit"
              className="inline-flex items-center gap-2 bg-acid text-white font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
              <Truck className="w-4 h-4" /> {busy ? "Booking…" : "Book Truck"}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="truck-bookings-list">
        {(bookings || []).map((b) => (
          <div key={b.id} className="border-2 border-obsidian bg-white p-5 flex items-start justify-between gap-4 flex-wrap hover:shadow-[6px_6px_0px_0px_#0B1E3A] transition-all duration-150" data-testid={`truck-card-${b.reference_no}`}>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 bg-obsidian text-sky-300 flex items-center justify-center"><Truck className="w-5 h-5" /></span>
              <div>
                <p className="font-mono text-sm font-bold">{b.reference_no}</p>
                <p className="text-xs text-zinc-500">
                  {b.pickup?.city} → {b.delivery?.city} · {b.weight_kg}kg · {b.pieces} pc · {b.goods_description}
                </p>
                <p className="text-xs font-mono text-zinc-400 mt-0.5 flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" /> Pickup {b.pickup_date} · booked {fmtDate(b.created_at)}
                </p>
                {b.driver_name && (
                  <p className="text-xs font-mono font-bold mt-1 text-obsidian">Driver: {b.driver_name} · {b.driver_phone} · {b.vehicle_number}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {b.status === "pending_allocation" && (
                <button
                  onClick={() => cancelBooking(b)}
                  disabled={busy === b.id}
                  data-testid={`truck-cancel-${b.reference_no}`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-wider px-3 py-1.5 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors duration-150 disabled:opacity-50"
                >
                  <XCircle className="w-3.5 h-3.5" /> {busy === b.id ? "…" : "Cancel"}
                </button>
              )}
              <StatusBadge status={b.status || "pending"} />
            </div>
          </div>
        ))}
        {bookings && bookings.length === 0 && (
          <div className="border-2 border-obsidian bg-white p-10 text-sm text-zinc-500" data-testid="trucks-empty">
            No truck bookings yet — hit Book Truck and we'll have a driver assigned in ~30 minutes.
          </div>
        )}
        {!bookings && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading bookings…</p>}
      </div>
    </div>
  );
}
